import { AssociatedService, IngressEvent, ServiceOperation } from '../../../../store/_api/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FlowConnector, FlowItem } from '../../../../_components/flow-builder/TypeDefs';
import { FLOW_CONFIG, initialState, journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { computeIsCompensateAction, findEmptyPositionOnCanvas, getFlowItemButtons, isRootNode, shouldConnectVertically, shouldItemBeStartNode } from '../../utility/helpers';
import { DraggingItemType, FlowItemDataMap, JourneyCanvasState, SelectedFlowItem, SidebarDataLoadingState, SidebarState } from '../../utility/TypeDefs';
import {
  FetchAllAssociatedServicesPayload,
  FetchServiceOperationsPayload,
  MoreServicesLoadedPayload,
  OpenSidebarAfterServiceReturnPayload,
  PickIngressEventPayload,
  PickServicePayload,
  ReplaceAllServicesListPayload,
  SearchItemsPayload,
  SetSearchDataPayload
} from '../TypeDefs';
import { getServiceCommonData, getStateForNewService } from './FlowItemsReducerActions';

/**
 * @method hasDuplicateAssociatedService
 * @description to check for duplicate associate services
 * @param service
 * @param flowConnectors
 * @param flowItemDataMap
 * @param selectedFlowItem
 * @returns true or false
 */
export const hasDuplicateAssociatedService = ({
  service,
  flowConnectors,
  flowItemDataMap,
  selectedFlowItem,
  serviceOperation,
  ingressEvent,
}: {
  service: AssociatedService,
  serviceOperation: ServiceOperation,
  ingressEvent: IngressEvent,
  flowConnectors: FlowConnector[],
  flowItemDataMap: FlowItemDataMap,
  selectedFlowItem: SelectedFlowItem,
}) => !!flowConnectors.find((connector) => (
  flowItemDataMap[connector.toNodeId].serviceName === service.serviceName
  && flowItemDataMap[connector.toNodeId].serviceVersion === service.version
  && flowItemDataMap[connector.toNodeId].serviceOperationName === serviceOperation.serviceOperation
  && flowItemDataMap[connector.toNodeId].ingressEvent === ingressEvent.ingressEventName
  && flowItemDataMap[connector.toNodeId].ingressEventVersion === Number(ingressEvent.ingressEventVersion)
  && connector.fromNodeId === selectedFlowItem.nodeId
));

/**
 * @method fetchAllAssociatedServices
 * @description get all associated services of the selected service
 * @param state Journey canvas state
 * @param services list of associated services
 * @returns associated services and sidebar data loading state
 */
export const fetchAllAssociatedServices = (state: JourneyCanvasState, {
  services
}: FetchAllAssociatedServicesPayload): JourneyCanvasState => ({
  ...state,
  associatedServices: [...services],
  sidebarDataLoadingState: SidebarDataLoadingState.NONE
});

/**
 * @method setSearchData
 * @description set input search to searchTerm
 * @param state Journey canvas state
 * @param data search inputs
 * @returns setting input search to searchTerm
 */
export const setSearchData = (state: JourneyCanvasState, {
  data
}: SetSearchDataPayload): JourneyCanvasState => ({
  ...state,
  searchTerm: data
});

/**
 * @method searchItems
 * @description show services based on input search
 * @param state Journey canvas state
 * @param data input search data
 * @returns list of service based on search
 */
export const searchItems = (state: JourneyCanvasState, {
  services
}: SearchItemsPayload): JourneyCanvasState => {
  if (state.searchTerm) {
    return {
      ...state,
      filteredServices: services,
      allServicesDataLoad: initialState.allServicesDataLoad,
      sidebarDataLoadingState: SidebarDataLoadingState.NONE,
    };
  }
  return {
    ...state,
    filteredServices: undefined,
    allServicesDataLoad: {
      ...initialState.allServicesDataLoad,
      page: Math.ceil(state.allServices.length / journeyCanvasConstants.allServicesPageSize) - 1, // restore page number for all services
    },
    sidebarDataLoadingState: SidebarDataLoadingState.NONE
  };
};

/**
 * @method checkDuplicateAssociatedService
 * @description Checks if there is a duplicate associated service for the selected flow item.
 * @param {SelectedFlowItem | undefined} selectedFlowItem - The currently selected flow item.
 * @param {DraggingItemType} type - The type of the dragging item.
 * @param {ServiceOperation | undefined} serviceOperation - The service operation.
 * @param {IngressEvent | undefined} ingressEvent - The ingress event.
 * @param {FlowConnector[]} flowConnectors - The list of flow connectors.
 * @param {FlowItemDataMap} flowItemDataMap - The map of flow item data.
 * @param {AssociatedService} service - The associated service.
 * @returns {boolean} - Returns true if there is a duplicate associated service, otherwise false.
 */
const checkDuplicateAssociatedService = (selectedFlowItem: SelectedFlowItem | undefined, type: DraggingItemType, serviceOperation: ServiceOperation | undefined, ingressEvent: IngressEvent | undefined, flowConnectors: FlowConnector[], flowItemDataMap: FlowItemDataMap, service: AssociatedService) => {
  // adding check to prevent duplicate associated service
  if (
    selectedFlowItem
    && type === DraggingItemType.ASSOCIATED_SERVICE
    && serviceOperation
    && ingressEvent
    && hasDuplicateAssociatedService({
      flowConnectors,
      flowItemDataMap,
      selectedFlowItem,
      service,
      serviceOperation,
      ingressEvent,
    })
  ) {
    return true;
  }

  return false;
};

/**
 * @method pickService
 * @description reducer function to pick a service from the service list
 * @param isCompensateAction
 * @param service
 * @param type
 * @returns state
 * @returns commonData
 * @returns isExternal
 * @returns isRoot
 * @returns isRootExternal
 * @returns orientation
 * @returns pickingService
 * @returns service
 * @returns uid
 * @returns x
 * @returns y
 * @returns prevFlowItem
 * @returns selectedFlowItem
 */
export const pickService = (state: JourneyCanvasState, {
  isCompensateAction,
  service,
  type,
  serviceOperation,
  ingressEvent,
}: PickServicePayload): JourneyCanvasState => {
  const pickingService = type === DraggingItemType.SERVICE;
  const { selectedFlowItem } = state;

  const isStartNodeCheckValid = shouldItemBeStartNode(selectedFlowItem, state.flowConnectors, state.prevStepCardUUID, state.flowItems[0], state.ingressNodeId);

  if (!isStartNodeCheckValid) {
    return state;
  }

  const isRoot = isRootNode(pickingService, state.prevStepCardUUID, selectedFlowItem, state.flowItems[0]);

  const isDuplicateAssociatedService = checkDuplicateAssociatedService(selectedFlowItem, type, serviceOperation, ingressEvent, state.flowConnectors, state.flowItemDataMap, service);
  if (isDuplicateAssociatedService) {
    return state;
  }

  // add a new flow item on click
  const { commonData, isExternal } = getServiceCommonData({
    isCompensateAction,
    service,
    type,
    serviceOperation,
    ingressEvent
  });

  if (!commonData) {
    return state;
  }

  if (commonData.isExternal && isRoot && state.prevStepCardUUID) {
    return {
      ...state,
      showExternalOnSubsequentStepModal: true
    };
  }

  let prevFlowItem: FlowItem | undefined;
  if (state.designingCEM && pickingService) {
    [prevFlowItem] = state.flowItems;
  } else if (selectedFlowItem?.nodeId) {
    prevFlowItem = state.flowItems.find((e) => e.nodeId === selectedFlowItem?.nodeId);
  }

  const isVertical = shouldConnectVertically(state.designingCEM, pickingService, state.designingStep, state.designingInitialStepJourney, state.flowItems.length);

  const { x, y } = findEmptyPositionOnCanvas(
    state.flowItems,
    isRoot && commonData.isExternal,
    prevFlowItem,
    true,
    isVertical ? Orientation.VERTICAL : FLOW_CONFIG.orientation
  );
  const isRootExternal = (isRoot && !state.designingCEM) ? commonData.isExternal : state.isRootExternal;
  return getStateForNewService({
    state,
    commonData,
    isExternal,
    isRoot,
    isRootExternal,
    orientation: isVertical ? Orientation.VERTICAL : Orientation.HORIZONTAL,
    pickingService,
    service,
    uid: commonData.nodeId,
    x,
    y,
    prevFlowItem,
    selectedFlowItem,
    isSubsequentStep: !!state.prevStepCardUUID
  });
};

/**
 * @method pickIngressEvent
 * @description add ingress event to flow item and set connector text and tooltip as ingress event
 * @param state Journey canvas state
 * @param serviceOps service operation of the selected flow item
 * @returns flowItems, flow connectors, flow item data map and selected flow item
 */
export const pickIngressEvent = (state: JourneyCanvasState, {
  serviceOps,
  ingressEvent
}: PickIngressEventPayload) => {
  const firstFlowItem = state.designingCEM ? state.flowItems[1] : state.flowItems[0];
  firstFlowItem.subTitle = serviceOps.serviceOperation;
  const flowItemData = state.flowItemDataMap[firstFlowItem.nodeId];
  if (!flowItemData) {
    return state;
  }
  flowItemData.ingressEvent = ingressEvent?.ingressEventName ?? '';
  flowItemData.ingressEventVersion = Number(ingressEvent?.ingressEventVersion ?? '1');
  flowItemData.serviceOperationName = serviceOps.serviceOperation;

  // recompute compensate flag based on selected ingress event and update buttons
  const svc = state.allServices.find(
    (s) => s.serviceName === flowItemData.serviceName && s.version === flowItemData.serviceVersion
  );

  const isCompensateAction = computeIsCompensateAction({
    designingCEM: state.designingCEM,
    serviceType: flowItemData.serviceType ?? svc?.serviceType,
    svcIsCompensateAction: svc?.isCompensateAction,
    ingressEvent,
  });

  flowItemData.isCompensateAction = isCompensateAction;
  firstFlowItem.buttons = getFlowItemButtons(
    false,
    isCompensateAction,
    state.showTransformPayloadModal,
    !!state.viewJourneyInitiated
  );
  // bump revision so FlowBuilder recreates CanvasElement and applies updated buttons
  firstFlowItem.revision = (firstFlowItem.revision ?? 0) + 1;

  const firstConnector = state.flowConnectors.length > 0 ? state.flowConnectors[0] : undefined;
  if (firstConnector) {
    firstConnector.text = ingressEvent.ingressEventName;
    firstConnector.displayEventVersion = Number(ingressEvent.ingressEventVersion);
    firstConnector.tooltip = ingressEvent.ingressEventName;
  }

  return {
    ...state,
    flowConnectors: firstConnector ? [...state.flowConnectors] : state.flowConnectors,
    flowItems: [...state.flowItems],
    flowItemDataMap: {
      ...state.flowItemDataMap,
      [firstFlowItem.nodeId]: flowItemData
    },
    selectedFlowItem: undefined,
  };
};

/**
 * @method hideSidebar
 * @description change sidebarState to none
 * @param state Journey canvas state
 * @returns sidebarState
 */
export const hideSidebar = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  sidebarState: SidebarState.NONE
});

/**
 * @method sidebarDataLoading
 * @description change sidebar data loading state to LOADING
 * @param state Journey canvas state
 * @returns sidebarDataLoadingState
 */
export const sidebarDataLoading = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  sidebarDataLoadingState: SidebarDataLoadingState.LOADING,
  // For CEM mode, preserve associated services, otherwise clear them
  associatedServices: state.designingCEM ? state.associatedServices : [],
  validation: {
    ...state.validation,
    sidebarInvalidSearch: false,
  }
});

/**
 * @method sidebarDataError
 * @description load sidebar data error
 * @param state Journey canvas state
 * @returns sidebarDataLoadingState
 */
export const sidebarDataError = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  sidebarDataLoadingState: SidebarDataLoadingState.ERROR,
});

/**
 * @method loadMoreServices
 * @description load more Services
 * @param state Journey canvas state
 * @returns allServicesDataLoad
 */
export const loadMoreServices = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  allServicesDataLoad: {
    loadingMore: true,
    page: state.allServicesDataLoad.page,
    noLoadMore: false,
    failed: false
  }
});

/**
 * @method moreServicesLoaded
 * @param state Journey canvas state
 * @param services list of services
 * @returns allServices, filteredServices and allServicesDataLoad
 */
export const moreServicesLoaded = (state: JourneyCanvasState, {
  services
}: MoreServicesLoadedPayload): JourneyCanvasState => ({
  ...state,
  allServices: state.searchTerm ? state.allServices : [...state.allServices, ...services],
  filteredServices: state.searchTerm ? [...(state.filteredServices ?? []), ...services] : state.filteredServices,
  allServicesDataLoad: {
    loadingMore: false,
    page: state.allServicesDataLoad.page + 1,
    noLoadMore: !services.length,
    failed: false
  }
});

/**
 * @method moreServicesLoadFailed
 * @description Failed to load more Services
 * @param state Journey canvas state
 * @returns allServicesDataLoad
 */
export const moreServicesLoadFailed = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  allServicesDataLoad: {
    loadingMore: false,
    page: state.allServicesDataLoad.page,
    noLoadMore: true,
    failed: true
  }
});

/**
 * @method fetchServiceOperations
 * @description get service operation of the specific service
 * @param state Journey canvas state
 * @param serviceOperations service operations
 * @returns serviceOperations
 */
export const fetchServiceOperations = (state: JourneyCanvasState, {
  serviceOperations
}: FetchServiceOperationsPayload): JourneyCanvasState => ({
  ...state,
  sidebarDataLoadingState: SidebarDataLoadingState.NONE,
  serviceOperations,
});

/**
 * @method setSidebarInvalidSearch
 * @description Sets the sidebar invalid search state.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const setSidebarInvalidSearch = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  validation: {
    ...state.validation,
    sidebarInvalidSearch: true,
  },
});

/**
 * @method setSideBarForCommentHistory
 * @description Set sidebarstate value to returned comment history
 * @param state Journey canvas state
 * @returns updated sidebar state
 */
export const setSideBarForCommentHistory = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  sidebarState: SidebarState.RETURNED_COMMENT_HISTORY
});

export const hideExternalOnSubsequentStepModal = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  showExternalOnSubsequentStepModal: false,
});

/**
 * Replace the full all-services list (e.g. after returning from Control Plane).
 */
export const replaceAllServicesList = (state: JourneyCanvasState, {
  services
}: ReplaceAllServicesListPayload): JourneyCanvasState => ({
  ...state,
  allServices: services,
  filteredServices: undefined,
  searchTerm: '',
  validation: {
    ...state.validation,
    sidebarInvalidSearch: false,
  },
  allServicesDataLoad: {
    loadingMore: false,
    page: 0,
    noLoadMore: services.length < journeyCanvasConstants.allServicesPageSize,
    failed: false,
  },
  sidebarDataLoadingState: SidebarDataLoadingState.NONE,
});

/**
 * Open the service / associated-service sidebar after a Control Plane return (step or CEM context
 * must already be active).
 *
 * Intentionally does NOT set `startSelected: true`. The user has not necessarily clicked the START
 * node — they may have left the canvas from a flow-item context where their selection wasn't
 * captured in the resume payload. Highlighting START here would make the "initial journey service"
 * appear selected on return, overriding the canvas focus the user expects. Leave the existing
 * `startSelected` value as-is so the canvas doesn't gain a spurious selection.
 */
export const openSidebarAfterServiceReturn = (state: JourneyCanvasState, {
  sidebar
}: OpenSidebarAfterServiceReturnPayload): JourneyCanvasState => ({
  ...state,
  sidebarState: sidebar,
});
